import { useQuery, UseQueryOptions } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

const QUERY_KEY_PREFIX = 'query-implementations';
const getQueryKey = (integrationId: number) =>
  `${QUERY_KEY_PREFIX}-${integrationId}`;

// The types defined below should be used by consumers using this
// `useQueryImplementations` hook. One of their goals is to replace
// developer-v3/platformSchema ultimately.
//
// developer-v3/platformSchema has schemas similar to the types below. But they
// aren't completely validated, and even ignored sometimes, because of
// incomplete FlowJS => Typescript migration, along with other nuance type bugs.
// It's challenging to fix them all. Therefore the goal is to have this query
// hook replace GraphQL and Entity along with developer-v3/platformSchema.

type Display = {
  description: string;
  hidden?: boolean;
  important?: boolean;
  label: string;
};
type FunctionSchema = string;
type AuthType = 'basic' | 'custom' | 'digest' | 'oauth1' | 'oauth2' | 'session';
type Request = {
  auth?: string[] | Record<string, null | string | number | boolean>;
  body?: null | string | Record<string, any> | any[];
  headers?: Record<string, null | string | number | boolean>;
  method?: 'DELETE' | 'GET' | 'HEAD' | 'PATCH' | 'POST' | 'PUT';
  params?: Record<string, null | string | number | boolean>;
  removeMissingValuesFrom?: Record<string, any>;
  serializeValueForCurlies?: FunctionSchema;
  skipEncodingChars?: boolean;
  skipThrowForStatus?: boolean;
  url?: string;
};
type InputField = {
  choices?: Record<string, string>;
  key: string;
  default?: string;
  dynamic?: string;
  helpText?: string;
  label: string;
  required: boolean;
};
type OutputField = {
  key: string;
  label?: string;
  list?: boolean;
  type?: string;
};
type Sample = Record<string, any>;
type Resource = string;
type ChoiceField =
  | Record<string, any>
  | string[]
  | { label: string; sample: string; value: string }[];
type Field = {
  altersDynamicFields: boolean;
  computed?: boolean;
  dynamic: Resource;
  search: Resource;
  choices: ChoiceField[];
  dict: boolean;
  children: Field[];
  list?: boolean;
  key: string;
  default: string;
  helpText: string;
  label: string;
  required: boolean;
  type: 'string';
  placeholder: string;
  inputFormat: string;
};
type Definition = {
  authentication: {
    type: AuthType;
    connectionLabel?: string; // | FunctionSchema | Request;
    fields: Field[];
    oauth2Config: {
      authorizeUrl: FunctionSchema;
      autoRefresh: true;
      getAccessToken: FunctionSchema;
      refreshAccessToken: FunctionSchema;
    };
    test: FunctionSchema; // | Request;
  };
  creates: Record<
    string,
    {
      display: Display;
      key: string;
      noun: string;
      operation: {
        inputFields?: InputField[];
        outputFields?: OutputField[];
        perform: FunctionSchema | Request;
        performGet?: FunctionSchema | Request;
        performResume?: FunctionSchema;
        sample: Sample;
      };
    }
  >;
  platformVersion: string;
  searchOrCreates: Record<
    string,
    {
      create: string;
      display: Display;
      key: string;
      search: string;
      searchUniqueInputToOutputConstraint: Record<string, string>;
      update?: string;
      updateInputFromSearchOutput: Record<
        string,
        Record<string, null | string | number | boolean>
      >;
    }
  >;
  searches: Record<
    string,
    {
      display: Display;
      key: string;
      noun: string;
      operation: {
        inputFields?: InputField[];
        outputFields?: OutputField[];
        perform: FunctionSchema | Request;
        performGet?: FunctionSchema | Request;
        performResume?: FunctionSchema;
        sample: Sample;
      };
    }
  >;
  triggers: Record<
    string,
    {
      display: Display;
      key: string;
      noun: string;
      operation:
        | {
            canPaginate?: boolean;
            inputFields?: InputField[];
            outputFields?: OutputField[];
            perform: FunctionSchema;
            resource?: string;
            sample: Sample;
            type?: 'polling';
          }
        | {
            canPaginate?: boolean;
            inputFields?: InputField[];
            outputFields?: OutputField[];
            perform: FunctionSchema;
            performList: FunctionSchema | Request;
            performSubscribe: FunctionSchema | Request;
            performUnsubscribe: FunctionSchema | Request;
            resource?: string;
            sample: Sample;
            type?: 'hook';
          };
    }
  >;
  version: string;
};

export type Implementation = {
  appId: string;
  // changelog: string;
  // coreNpmVersion: string;
  // date: string;
  deployment: string;
  definition: Definition;
  // deprecationDate?: string;
  // environment: Record<string, string>;
  // etag?: string;
  // id: string;
  // inviteUrl: string;
  isUi: boolean;
  // oauthRedirectUri?: string;
  // platformVersion: string;
  selectedApi: string;
  // submitStatus?: string;
  // userCount?: number;
  // userCountUpdatedAt?: string;
  version: string;
};

function formatResponseBody(body: any): Implementation[] {
  const { objects: implementations } = body;
  return implementations.map((implementation: any) => ({
    appId: implementation.app_id.toString(),
    // changelog: implementation.changelog,
    // coreNpmVersion: implementation.core_npm_version,
    // date: implementation.date, // Creation date.
    definition: implementation.definition,
    deployment: implementation.deployment,
    // deprecateionDate: implementation.deprecation_date,
    // environment: implementation.environment,
    // etag: implementation.etag,
    // id: implementation.id,
    // inviteUrl: implementation.invite_url,
    isUi: implementation.is_ui,
    // oauthRedirectUri: implementation.oauth_redirect_uri,
    // platformVersion: implementation.platform_version,
    selectedApi: implementation.selected_api,
    // userCount: implementation.user_count,
    version: implementation.version,
  }));
}

// This uses the factory pattern since we need the query function in
// `useQueryIntegrationsImplementations` to run multiple queries with
// `useQueries`, which requires to build the queries inline.
function getQueryFn(integrationId: number) {
  const url = new URL(
    `api/platform/cli/apps/${encodeURIComponent(integrationId)}/versions`,
    ZAPIER_APP_BASE_URL
  );

  return async () => formatResponseBody(await fetchJson(url.href));
}

// This uses the factory pattern to get query options since we need them in
// `useQueryIntegrationsImplementations` to run multiple queries with
// `useQueries`, which requires to build the queries inline.
function getQueryOptions({
  options,
  notify,
  queryKey,
}: {
  options?: UseQueryOptions<Implementation[], Error>;
  notify: ReturnType<typeof useNotify>;
  queryKey: string;
}) {
  return {
    onError: async (error: any) => notify.failure(error.message, queryKey),
    ...options,
  };
}

function useQueryImplementations(
  args: { integrationId: number },
  options?: UseQueryOptions<Implementation[], Error>
) {
  const { integrationId } = args;
  const notify = useNotify();

  const queryKey = getQueryKey(integrationId);

  const query = useQuery({
    queryKey,
    queryFn: getQueryFn(integrationId),
    ...getQueryOptions({ notify, options, queryKey }),
  });

  return { ...query, queryKey };
}

export default useQueryImplementations;
export { getQueryFn, getQueryKey, getQueryOptions, QUERY_KEY_PREFIX };
